import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, Camera, Check, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6 } }) };

export default function UGCUploadPage() {
  const { eventId } = useParams();
  const fileRef = useRef(null);
  const [event, setEvent] = useState(null);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  const [approved, setApproved] = useState([]);

  useEffect(() => {
    api.get(`/events/${eventId}`).then(r => setEvent(r.data)).catch(() => {});
    api.get(`/ugc/event/${eventId}`).then(r => setApproved(r.data || [])).catch(() => {});
  }, [eventId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!guestName) { toast.error("Please enter your name first."); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("event_id", eventId);
      formData.append("guest_name", guestName);
      formData.append("guest_email", guestEmail);
      const res = await api.post("/ugc/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded! It'll appear after approval.");
      setUploaded(prev => [...prev, { id: res.data.id, filename: file.name }]);
    } catch (err) { toast.error(errorMessage(err, "Upload failed.")); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div data-testid="ugc-upload-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-6">
          <motion.div initial="hidden" animate="visible" className="text-center mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-4">Share Your Moments</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl font-light text-white mb-3">
              {event?.title || "Event"} <span className="italic text-gold">Gallery</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/40 font-body text-sm">Upload your photos from the event. The best shots will be featured in the official gallery!</motion.p>
          </motion.div>

          <motion.div initial="hidden" animate="visible" className="border border-white/10 bg-luxury-charcoal p-8" data-testid="ugc-form">
            <div className="space-y-4 mb-6">
              <input value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Your Name *" className="w-full bg-white/5 border border-white/10 text-white text-sm px-4 py-3 font-body focus:outline-none focus:border-gold/30" data-testid="ugc-name" />
              <input value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="Your Email (optional)" className="w-full bg-white/5 border border-white/10 text-white text-sm px-4 py-3 font-body focus:outline-none focus:border-gold/30" data-testid="ugc-email" />
            </div>
            <button onClick={() => fileRef.current?.click()} disabled={uploading || !guestName} className="w-full border-2 border-dashed border-white/15 hover:border-gold/30 py-10 flex flex-col items-center gap-3 transition-colors disabled:opacity-40" data-testid="ugc-upload-btn">
              {uploading ? (
                <div className="text-gold font-body text-sm animate-pulse">Uploading...</div>
              ) : (
                <>
                  <Camera size={32} className="text-gold/50" />
                  <span className="text-white/50 font-body text-sm">Tap to upload a photo</span>
                  <span className="text-white/20 font-body text-[10px]">Max 15MB per photo</span>
                </>
              )}
            </button>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handleUpload} className="hidden" />

            {uploaded.length > 0 && (
              <div className="mt-6 space-y-2">
                <p className="text-gold/60 text-xs font-body uppercase tracking-wider">Your Uploads</p>
                {uploaded.map(u => (
                  <div key={u.id} className="flex items-center gap-2 text-white/50 text-xs font-body">
                    <Check size={12} className="text-green-400" /> {u.filename} — Pending approval
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {approved.length > 0 && (
            <div className="mt-12">
              <p className="text-gold/60 text-xs font-body uppercase tracking-wider mb-4">Guest Gallery ({approved.length} photos)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {approved.map((a, i) => (
                  <motion.div key={a.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="aspect-square overflow-hidden">
                    <img src={a.url} alt={a.guest_name} className="w-full h-full object-cover" loading="lazy" />
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
